/**
 * Utility functions for date validation in the rental booking system
 */

/**
 * Checks if same-day booking is allowed based on the 11:30 AM cutoff time
 * @param {Date} date - The date to check
 * @returns {boolean} - True if same-day booking is allowed, false otherwise
 */
export const isSameDayBookingAllowed = (date) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  // If it's not today, allow the date
  if (targetDate.getTime() !== today.getTime()) {
    return true;
  }
  
  // If it's today, check if it's before 11:30 AM
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  
  // Allow if current time is before 11:30 AM
  return currentHour < 11 || (currentHour === 11 && currentMinute < 30);
};

/**
 * Gets the minimum allowed date for booking based on the 11:30 AM cutoff
 * @returns {Date} - The minimum allowed date for booking
 */
export const getMinBookingDate = () => {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  
  // If it's after 11:30 AM, disable same-day booking
  if (currentHour > 11 || (currentHour === 11 && currentMinute >= 30)) {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  }
  
  return now;
};

/**
 * Checks if a specific date and time is available for booking
 * @param {Date} date - The date to check
 * @param {number} hour - The hour (0-23)
 * @param {number} minute - The minute (0-59)
 * @returns {boolean} - True if the time slot is available
 */
export const isTimeSlotAvailable = (date, hour = 0, minute = 0) => {
  const now = new Date();
  const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // If it's not today, allow the time slot
  if (targetDate.getTime() !== today.getTime()) {
    return true;
  }
  
  // If it's today, check if the requested time is after 11:30 AM
  if (hour > 11 || (hour === 11 && minute >= 30)) {
    return true;
  }
  
  // If it's before 11:30 AM, check if current time is before 11:30 AM
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  
  return currentHour < 11 || (currentHour === 11 && currentMinute < 30);
};

/**
 * Gets the default start date for booking based on the 11:30 AM cutoff
 * If it's after 11:30 AM, returns tomorrow's date, otherwise returns today's date
 * @returns {Date} - The default start date for booking
 */
export const getDefaultStartDate = () => {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  
  // If it's after 11:30 AM, return tomorrow's date
  if (currentHour > 11 || (currentHour === 11 && currentMinute >= 30)) {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0); // Reset time to start of day
    return tomorrow;
  }
  
  // Otherwise, return today's date
  const today = new Date(now);
  today.setHours(0, 0, 0, 0); // Reset time to start of day
  return today;
};
